import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ChefHat, LogOut, RefreshCw, Clock, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { orderApi } from "../lib/orderApi";

const STATUS_CONFIG = {
  placed: { label: "New Order", color: "bg-blue-100 text-blue-800 border-blue-200", next: "accepted" },
  accepted: { label: "Preparing", color: "bg-amber-100 text-amber-800 border-amber-200", next: "served" },
  served: { label: "Served", color: "bg-emerald-100 text-emerald-800 border-emerald-200", next: null }
};

export default function KitchenDashboard() {
  const navigate = useNavigate();
  const { logout } = useAuth();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchOrders = useCallback(async () => {
    try {
      const data = await orderApi.getAllOrders();
      setOrders(data || []);
    } catch {
      console.warn("Could not fetch orders");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
    const interval = setInterval(fetchOrders, 4000);
    return () => clearInterval(interval);
  }, [fetchOrders]);

  const updateStatus = async (id, status) => {
    try {
      await orderApi.updateOrderStatus(id, status);
      toast.success("Order status updated");
      fetchOrders();
    } catch {
      toast.error("Failed to update status");
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFDF9] p-4 sm:p-6 max-w-4xl mx-auto space-y-6">
      {/* HEADER */}
      <div className="flex items-center justify-between pb-4 border-b border-stone-200">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-amber-100 text-amber-700 flex items-center justify-center">
            <ChefHat className="w-6 h-6" />
          </div>
          <div>
            <h1 className="font-extrabold text-xl text-[#2C3E50]">Kitchen Live Display</h1>
            <p className="text-xs text-stone-500">Live incoming table orders</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchOrders} className="h-9">
            <RefreshCw className="w-4 h-4" />
          </Button>

          <Button variant="outline" size="sm" onClick={() => navigate("/counter")} className="text-xs h-9">
            Counter Panel
          </Button>

          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              logout();
              navigate("/login");
            }}
            className="text-xs h-9 text-stone-500"
          >
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* ORDERS LIST */}
      <div className="space-y-4">
        {loading ? (
          <p className="text-center text-xs text-stone-400 py-10">Loading orders...</p>
        ) : orders.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-3xl border border-stone-200 p-6">
            <ChefHat className="w-12 h-12 text-stone-300 mx-auto mb-2" />
            <p className="font-bold text-stone-600">No active kitchen orders</p>
            <p className="text-xs text-stone-400 mt-1">New incoming orders will appear here automatically.</p>
          </div>
        ) : (
          orders.map((order) => {
            const config = STATUS_CONFIG[order.status] || STATUS_CONFIG.placed;

            return (
              <motion.div
                key={order.id}
                className="bg-white p-5 rounded-3xl border border-stone-200/80 shadow-xs space-y-3"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                {/* HEADER */}
                <div className="flex items-center justify-between pb-2 border-b border-stone-100">
                  <div>
                    <h2 className="font-extrabold text-base text-[#2C3E50]">
                      Order #{order.order_number}
                    </h2>
                    <span className="text-xs font-bold text-sky-600">
                      {order.table_name || "Dine-in"}
                    </span>
                  </div>

                  <div className="text-right space-y-1">
                    <Badge className={`text-[10px] font-bold border ${config.color}`}>
                      {config.label}
                    </Badge>
                    <p className="text-[11px] text-stone-400">
                      <Clock className="w-3 h-3 inline mr-1" />
                      {new Date(order.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>

                {/* ITEMS */}
                <div className="space-y-1.5 py-1">
                  {order.items?.map((item) => (
                    <div key={item.id} className="text-sm flex justify-between items-center text-stone-700">
                      <span className="font-medium">
                        <strong className="text-[#2C3E50]">{item.quantity}×</strong> {item.product_name}
                      </span>
                    </div>
                  ))}
                </div>

                {order.notes && (
                  <div className="p-2.5 rounded-xl bg-amber-50 border border-amber-100 text-xs text-amber-800">
                    <strong>Note:</strong> {order.notes}
                  </div>
                )}

                {/* ACTION */}
                {config.next && (
                  <Button
                    className="w-full bg-sky-500 hover:bg-sky-600 text-white font-bold text-xs py-2.5 rounded-xl"
                    onClick={() => updateStatus(order.id, config.next)}
                  >
                    {config.next === "accepted" && "Accept & Start Preparing"}
                    {config.next === "served" && "Mark Order as Served"}
                  </Button>
                )}
              </motion.div>
            );
          })
        )}
      </div>
    </div>
  );
}